// 3rd party libraries
import React, { ReactElement, useEffect, useMemo, useRef, useState } from 'react';
import Table from 'rc-table';
import { DefaultRecordType, RenderExpandIcon } from 'rc-table/lib/interface';
import styled from 'styled-components';
import { cloneDeep } from 'lodash';

// Components
import Title from './Title';
import { BodyRow, CustomExpandIcon } from './Row';
import { StyledCell, StyledHeaderCell } from './Cell';
import { StyledHeaderWrapper, StyledHeaderRow, StyledHeaderWrapperNoChildren } from './Headers';
import TableModal from './Modal';
import { Button, Loader } from 'components';

// Types
import { TableProps } from 'types';
import {
  CreateNewWidgetItemProps,
  WidgetTableDropdownItem,
  WidgetTableProps
} from 'types/machine-health';
import { WidgetType } from 'types/protein';

// Context
import { useWidgetTableContext } from './WidgetTableContext';

// Constants
import { MaxTagLength, RowIdDelimiter } from 'constants/machineConfig';
import { testMatrixTableTypes, testWidgetTypes } from 'constants/testdata/protein';

// Helpers
import { addRowCrudButtons, createLocalItem, editColumnConfigs } from './helpers';
import { DeleteIcon } from 'icons/indexed-icons/DeleteIcon';
import CheckCircle from 'icons/indexed-icons/CheckCircle';

/* Styled components */

// Style the base rc-table component (which is a actually a div)
const StyledTableComponent = styled(Table)<{
  borderBottomRow?: boolean;
  cellPadding?: string;
  hideScroll?: boolean;
  isExpanded?: boolean;
  outerBorderColor?: string;
  tableHeight?: number;
}>`
  border-radius: 0.5rem;
  border: ${({ theme }) => theme.colors.borders.border01.border};
  ${({ outerBorderColor }) => outerBorderColor && `border-color: ${outerBorderColor}`};

  table {
    border-spacing: 0;
    border-radius: 0.5rem;
  }

  .rc-table-container {
    border-radius: 0.5rem;
    overflow: ${({ isExpanded }) => (isExpanded ? 'visible' : 'hidden')};
    ${({ isExpanded, tableHeight }) =>
      isExpanded ? `max-height: ${tableHeight}px;` : `max-height: 0px;`}
  }

  & .rc-table-row-level-1,
  .rc-table-row-level-2 {
    background-color: transparent;
    height: 3rem;

    th:first-child {
      padding-left: 1.75rem;
    }
  }

  & .rc-table-row-level-2 {
    th:first-child {
      padding-left: 2.5rem;
    }
  }

  tbody {
    tr {
      th:last-child {
        padding-left: 0;
      }
    }

    tr:last-child {
      border-bottom: none;
      & th,
      td {
        border-bottom: none;
        border-bottom-left-radius: 0.5rem;
        border-bottom-right-radius: 0.5rem;
      }
    }
  }

  .rc-table-cell {
    padding: ${({ cellPadding }) => cellPadding ?? ''};
  }

  .rc-table-body {
    ${({ hideScroll }) =>
      hideScroll
        ? `scrollbar-width: none;
        &::-webkit-scrollbar {
          display: none;
        }
        -ms-overflow-style: none;
        `
        : ``}
  }

  .rc-table-cell-scrollbar {
    ${({ hideScroll }) => (hideScroll ? `padding: 0;` : ``)}
  }
`;

const StyledTable = styled.table`
  border-bottom-left-radius: 0.5rem;
  border-bottom-right-radius: 0.5rem;
  border-collapse: collapse;
  border-top: none;
  min-width: 100%;

  .rc-table-expand-icon-col {
    width: 1rem;
  }
`;

const StyledTableNoName = styled.table`
  min-width: 100%;
  border-collapse: collapse;

  .rc-table-expand-icon-col {
    width: 1rem;
  }
`;

const StyledNoDataMessage = styled.div`
  padding-left: 1rem;
`;

const StyledFileContent = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;
interface TagImagefieldData {
  id: string;
  imageSize: number | undefined;
  imageUrl: string | undefined;
  imageFile: null;
  markedForDeleted: boolean;
}

function findMatchingWidgets(data) {
  const matchesItem: TagImagefieldData[] = [];

  function recurse(items) {
    if (!Array.isArray(items)) return;

    for (const item of items) {
      if (item.widgetType === WidgetType.TagGroup) {
        matchesItem.push({
          id: item.id,
          imageSize: item.imageSize,
          imageUrl: item.imageUrl,
          imageFile: null,
          markedForDeleted: false
        });
      }

      if (item.members && Array.isArray(item.members)) {
        recurse(item.members);
      }
    }
  }

  recurse(data);

  return matchesItem;
}

const WidgetTable = ({
  columnConfigs,
  copyTable,
  data,
  displayOnlyRows,
  hideCrudButtons = false,
  hideScroll,
  isDataLoading,
  parent,
  rowKey,
  scroll,
  setIsDirty,
  shouldRenderTags,
  tags,
  showUploadImageRow,
  onFileUpdate,
  isDeleteImage,
  setIsDeleteImage
}: WidgetTableProps): ReactElement => {
  const {
    localTableNames,
    localTableRows,
    modalType,
    setLocalTableActive,
    setLocalTableNames,
    setLocalTableRows,
    setUploadTagsImage,
  } = useWidgetTableContext();

  const resolvedRowKey = rowKey ?? 'key';

  const [dataHasChildren, setDataHasChildren] = useState(false);
  const [editRow, setEditRow] = useState<null | string>(null);
  const [expandedRows, setExpandedRows] = useState<Record<string, boolean>>({});
  const [isEditingName, setIsEditingName] = useState(false);
  const [nameIsExpanded, setNameIsExpanded] = useState(false);
  const [tableHeight, setTableHeight] = useState(0);
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);

  const handleFileDelete = () => {
    setIsDeleteImage(true);
    setUploadedFile(null);
    if (onFileUpdate) {
      onFileUpdate(null);
    }
    setIsDirty(true);
  };

  const handleFileUpdate = (file: File | null) => {
    if (file) {
      setIsDeleteImage(false);
    }

    setUploadedFile(file);
    if (onFileUpdate) {
      onFileUpdate(file);
    }
  };

  const { active, editable, id, name, toggleActive } = parent ?? {
    active: false,
    editable: false,
    id: '',
    name: '',
    toggleActive: false
  };

  const { isCopyTable, handleCopyCallback, copyWidgetType } = copyTable ?? {};

  useEffect(() => {
    setLocalTableNames((prev) => [...prev, { id, name: name || '' }]);
    setLocalTableActive((prev) => [...prev, { id, active: !!active }]);
  }, [name, active]);

  useEffect(() => {
    if (onFileUpdate) {
      onFileUpdate(uploadedFile);
    }
  }, [uploadedFile]);

  const hasData = useRef(false);

  useEffect(() => {
    if (hasData.current && shouldRenderTags) {
      return;
    }

    if (data) {
      hasData.current = true;
    }

    data &&
      setLocalTableRows((prev) => {
        const next = [...prev];

        const insertLocation = next.findIndex((item) => item.id === id);
        const insertData = { ...parent, members: cloneDeep(data) };
        insertLocation === -1 ? next.push(insertData) : (next[insertLocation] = insertData);
        return next;
      });
  }, [data]);

  const localRows = useMemo(() => {
    return localTableRows.find((item) => item.id === id)?.members || [];
  }, [localTableRows, id]);

  const matchingWidgets = useMemo(() => {
    return findMatchingWidgets(localTableRows);
  }, [localTableRows]);

  useEffect(() => {
    tags?.forEach((tag) => {
      tag.disabled = false;
    });

    localTableRows.forEach((item) => {
      item?.members?.forEach((child) => {
        child.tags?.forEach((childTag) => {
          const memberTagMatch = tags?.find((option) => option.id === childTag?.id);
          memberTagMatch && (memberTagMatch.disabled = true);
        });
        child.members?.forEach((grandChild) => {
          grandChild.tags?.forEach((grandChildTag) => {
            const tagMatch = tags?.find((option) => option.id === grandChildTag?.id);
            tagMatch && (tagMatch.disabled = true);
          });
        });
      });
    });

  setUploadTagsImage(matchingWidgets)
  
  }, [localTableRows]);

  const rowsWithButtons = useMemo(() => {
    const { nextRowsWithButtons, hasChildren } = addRowCrudButtons({
      copyWidgetType,
      handleCopyCallback,
      hideCrudButtons,
      isCopyTable: !!isCopyTable,
      rows: localRows,
      setEditRow,
      setIsDirty,
      setIsEditingName,
      shouldRenderTags,
      tabId: id
    });

    setDataHasChildren(hasChildren);

    if (isCopyTable) {
      return nextRowsWithButtons;
    }

    const buttonRow = {
      actions: <></>,
      canBeExpanded: false,
      children: undefined,
      tagsZones: ''
    };

    if (shouldRenderTags) {
      nextRowsWithButtons.forEach((row) => {
        if (
          row.widgetType === WidgetType.MatrixWidget ||
          row.widgetType === WidgetType.MachineOverview ||
          row.widgetType === WidgetType.MachineOverviewFlyout
        ) {
          row.children?.forEach((child) => {
            const childButtonRow = {
              ...buttonRow,
              id: `${WidgetType.TagRow}${RowIdDelimiter}${child.id}`,
              key: `${WidgetType.TagRow}${RowIdDelimiter}${child.id}`
            };
            const tagCount = child.tags?.length || 0;
            child.children
              ? tagCount < MaxTagLength && child.children.push(childButtonRow)
              : (child.children = [childButtonRow]);
          });
        }

        const tagRow = {
          ...buttonRow,
          id: `${WidgetType.TagRow}${RowIdDelimiter}${row.id}`,
          key: `${WidgetType.TagRow}${RowIdDelimiter}${row.id}`
        };

        let tagCount = 0;
        row.children?.forEach((child) => {
          child.isTag && tagCount++;
        });

        if (
          row.children &&
          (row.widgetType === WidgetType.MachineOverview ||
            row.widgetType === WidgetType.MachineOverviewFlyout)
        ) {
          let insertLocation = -1;

          for (let i = row.children.length - 1; i >= 0; i--) {
            if (row.children[i].isTag) {
              insertLocation = i;
              break;
            }
          }

          insertLocation >= 0
            ? tagCount < MaxTagLength && row.children.splice(insertLocation + 1, 0, tagRow)
            : row.children.unshift(tagRow);
        } else if (tagCount < MaxTagLength && row.widgetType !== WidgetType.MatrixWidget) {
          row.children = row.children ? [...row.children, tagRow] : [tagRow];
        }
      });
    } else {
      const matrixGroupLocation = localRows.findIndex(
        (item) => item.widgetType === WidgetType.MatrixWidgetGroup
      );

      const insertLocation =
        matrixGroupLocation + (localRows?.[matrixGroupLocation]?.members?.length || 0);

      let matrixWidgetCount = 0;
      nextRowsWithButtons.forEach((row) => {
        if (row.widgetType === WidgetType.MatrixWidget) {
          matrixWidgetCount += 1;
        }
      });

      insertLocation >= 0 &&
        (parent?.label === 'MP_HMI_MAIN' ? matrixWidgetCount < 15 : matrixWidgetCount < 5) &&
        nextRowsWithButtons.splice(insertLocation, 0, {
          actions: <></>,
          children: undefined,
          id: WidgetType.WidgetRow,
          key: WidgetType.WidgetRow,
          tagsZones: ''
        });
    }

    for (let i = 0; i < nextRowsWithButtons.length; i++) {
      const row = nextRowsWithButtons[i];
      if (
        row.widgetType === WidgetType.MatrixWidget ||
        row.widgetType === WidgetType.MachineOverview ||
        row.widgetType === WidgetType.MachineOverviewFlyout
      ) {
        nextRowsWithButtons.splice(i + 1, 0, {
          ...buttonRow,
          id: `${WidgetType.TagGroupRow}${RowIdDelimiter}${row.id}`,
          key: `${WidgetType.TagGroupRow}${RowIdDelimiter}${row.id}`
        });
      }
    }


    nextRowsWithButtons[0]?.children &&
      displayOnlyRows &&
      (nextRowsWithButtons[0].children = [...displayOnlyRows, ...nextRowsWithButtons[0]?.children]);

    // Add displayOnlyRows at the top level if provided
    return displayOnlyRows ? [...displayOnlyRows, ...nextRowsWithButtons] : nextRowsWithButtons;
  }, [localRows, isEditingName, uploadedFile, isDeleteImage]);

  const transformedColumns = useMemo(() => {
    const nextEditColumns = editColumnConfigs({
      columnConfigs,
      dataSize: data?.length || 0,
      editRow,
      setEditRow,
      setIsDirty,
      setLocalTableRows,
      shouldRenderTags,
      tabId: id,
      tags
    });
    return nextEditColumns;
  }, [columnConfigs, data, editRow, isEditingName]);

  useEffect(() => {
    if (isDataLoading) {
      return setTableHeight(175);
    }
    const rem = 16;
    let totalRows = 0;
    rowsWithButtons.forEach((row) => {
      totalRows += 1;
      expandedRows[row.id] && (totalRows += row.children?.length || 0);

      row?.children?.forEach((child) => {
        expandedRows[child.id] && (totalRows += child.children?.length || 0);
      });
    });
    const height = (totalRows || 1) * rem * 4 + 64;
    setTableHeight(height);
  }, [rowsWithButtons, expandedRows, isDataLoading]);

  const handleCreateNewItem = ({
    isTag,
    parentId,
    tabId,
    value,
    widgetType,
    file
  }: CreateNewWidgetItemProps) => {
    const isAddTagGroupRow = modalType === WidgetType.TagGroupRow;
    const isAddWidgetRow = modalType === WidgetType.WidgetRow;

    setLocalTableRows?.((prev) =>
      createLocalItem({
        editRow,
        isAddTagGroupRow,
        isAddWidgetRow,
        isTag,
        localRows,
        parentId,
        prev,
        tabId,
        value,
        widgetType,
        file
      })
    );

    if (onFileUpdate) {
      onFileUpdate(file);
    }
  };

  const components = useMemo(() => {
    return {
      table: name ? StyledTable : StyledTableNoName,
      header: {
        wrapper: dataHasChildren ? StyledHeaderWrapper : StyledHeaderWrapperNoChildren,
        row: StyledHeaderRow,
        cell: StyledHeaderCell
      },
      body: {
        row: BodyRow,
        cell: StyledCell
      }
    };
  }, [dataHasChildren]);

  const modalOptionMap: {
    [key in WidgetType]?: { value: string; label: string }[] | WidgetTableDropdownItem[];
  } = {
    [WidgetType.TagRow]: tags || [],
    [WidgetType.TagGroupRow]: testWidgetTypes,
    [WidgetType.WidgetRow]: testMatrixTableTypes
  };

  return (
    <>
      <StyledTableComponent
        columns={transformedColumns}
        components={components}
        data={rowsWithButtons}
        emptyText={
          isDataLoading ? <Loader size={40} /> : <StyledNoDataMessage>No data</StyledNoDataMessage>
        }
        expandable={{
          expandIcon: CustomExpandIcon as unknown as RenderExpandIcon<DefaultRecordType>,
          expandIconColumnIndex: transformedColumns.length - 1
        }}
        hideScroll={hideScroll}
        isExpanded={!name ? true : nameIsExpanded}
        onExpand={(expanded, record) => {
          setExpandedRows((prev) => ({
            ...prev,
            [record.id]: expanded
          }));
        }}
        onRow={() => {
          return {
            editRow,
            handleCreateNewItem,
            setEditRow,
            setIsDirty,
            tabId: id
          } as React.HTMLAttributes<TableProps>;
        }}
        rowKey={resolvedRowKey}
        scroll={scroll}
        tableHeight={tableHeight}
        title={() => {
          return name ? (
            <Title
              editable={editable}
              id={id}
              isEditingName={isEditingName}
              isExpanded={nameIsExpanded}
              name={localTableNames.find((item) => item.id === id)?.name || ''}
              onExpandCallback={() => setNameIsExpanded((prev) => !prev)}
              setEditRow={setEditRow}
              setIsDirty={setIsDirty}
              setIsEditingName={setIsEditingName}
              toggleActive={toggleActive}
            />
          ) : null;
        }}
      />
      <TableModal
        hideFields={{ type: true }}
        modalType={modalType}
        options={(modalOptionMap[modalType] as WidgetTableDropdownItem[]) ?? []}
        submitCallback={handleCreateNewItem}
        tabId={id}
        onFileUpdate={setUploadedFile}
      />
    </>
  );
};

export default WidgetTable;
